import { algoliasearch } from 'algoliasearch'
import {
  algoliaAppId,
  algoliaSearchApiKey,
  algoliaEnvironmentIndex,
  isAlgoliaSearchConfigured,
} from '@/plugins/algolia/client'
import type { AppBranding, RelatedItem } from '@/plugins/algolia/types'

// Only initialize if credentials are available
const algoliaClient = isAlgoliaSearchConfigured()
  ? algoliasearch(algoliaAppId, algoliaSearchApiKey)
  : null

// === Typed Metadata Interfaces ===
// Each collection type stores specific related content in metadata

export type ReviewMetadata = {
  relatedBestPages: RelatedItem[]
  relatedComparisons: RelatedItem[]
  relatedBlogs: RelatedItem[]
  relatedDeal: RelatedItem | null
  relatedAlternativePage: RelatedItem | null
  relatedAlternativeApps: RelatedItem[]
}

export type BestMetadata = {
  relatedApps: RelatedItem[]
}

export type ComparisonMetadata = {
  relatedApps: RelatedItem[]
}

export type AlternativeMetadata = {
  relatedAlternativeApps: RelatedItem[]
}

export type DealMetadata = {
  relatedApp: RelatedItem
  eligibility: string | null
  promoCode: string | null
}

export type DealInfoMetadata = {
  relatedApp: RelatedItem
  relatedDeals: Array<{ name: string; eligibility: string | null }>
}

export type CourseMetadata = {
  relatedApp: RelatedItem
}

export type AlgoliaMetadata =
  | ReviewMetadata
  | BestMetadata
  | ComparisonMetadata
  | AlternativeMetadata
  | DealMetadata
  | DealInfoMetadata
  | CourseMetadata
  | Record<string, unknown>

export type AlgoliaSearchResult = {
  objectID: string
  collectionSlug: string
  collectionDocId: string | number
  // Routing
  slug: string
  // Display fields
  name: string
  imageUrl: string | null
  website: string | null
  icon?: string | null
  // App branding (for collections with app icons)
  appBranding?: AppBranding | null
  // Searchable fields
  searchable1: string
  searchable2: string
  searchable3: string
  searchable4: string
  searchable5: string
  searchable6: string
  searchable7?: string
  // Ranking fields
  globalBoost: number
  collectionPriority: number
  isFeatured: boolean
  // Collection-specific metadata (typed per collection)
  // Note: This is deprecated - metadata is now spread at top level
  metadata?: AlgoliaMetadata
  // Related content fields (spread at top level by Algolia plugin)
  // These are the actual fields as stored in Algolia
  relatedApps?: RelatedItem[]
  relatedBestPages?: RelatedItem[]
  relatedComparisons?: RelatedItem[]
  relatedBlogs?: RelatedItem[]
  relatedDeal?: RelatedItem | null
  relatedAlternativePage?: RelatedItem | null
  relatedAlternativeApps?: RelatedItem[]
  relatedApp?: RelatedItem
  relatedDeals?: Array<{ name: string; eligibility: string | null }>
  // Legacy fields for backwards compatibility during transition
  // TODO: Remove after full migration
  primary?: string
  secondary?: string
  tertiary?: string
}

// Collection types that can be filtered
export type SearchableCollectionSlug =
  | 'review'
  | 'alternative'
  | 'best'
  | 'blog'
  | 'comparison'
  | 'course'
  | 'page'
  | 'stack'
  | 'video'
  | 'deal_info'
  | 'deal'

export type AlgoliaSearchResponse = {
  hits: AlgoliaSearchResult[]
  nbHits: number
  page: number
  nbPages: number
  hitsPerPage: number
}

export type SearchOptions = {
  hitsPerPage?: number
  page?: number
  // Filter to specific collection types (e.g., ['review', 'comparison'])
  collectionTypes?: SearchableCollectionSlug[]
  // Only return featured items
  featuredOnly?: boolean
}

/**
 * Search Algolia index from the client side
 */
export async function searchAlgolia(
  query: string,
  options: SearchOptions = {},
): Promise<AlgoliaSearchResponse | null> {
  if (!algoliaClient || !algoliaEnvironmentIndex) {
    console.warn('Algolia client not configured - missing NEXT_PUBLIC_ALGOLIA_* env vars')
    return null
  }

  try {
    // Build filters array
    const filters: string[] = []

    // Collection type filter
    if (options.collectionTypes && options.collectionTypes.length > 0) {
      const collectionFilter = options.collectionTypes
        .map((type) => `collectionSlug:${type}`)
        .join(' OR ')
      filters.push(`(${collectionFilter})`)
    }

    // Featured filter
    if (options.featuredOnly) {
      filters.push('isFeatured:true')
    }

    const response = await algoliaClient.searchSingleIndex<AlgoliaSearchResult>({
      indexName: algoliaEnvironmentIndex,
      searchParams: {
        query,
        hitsPerPage: options.hitsPerPage ?? 10,
        page: options.page ?? 0,
        filters: filters.length > 0 ? filters.join(' AND ') : undefined,
      },
    })

    return {
      hits: response.hits,
      nbHits: response.nbHits ?? 0,
      page: response.page ?? 0,
      nbPages: response.nbPages ?? 0,
      hitsPerPage: response.hitsPerPage ?? 10,
    }
  } catch (error) {
    console.error('Algolia search error:', error)
    return null
  }
}

// Re-export for convenience - use this for client-side code
export { isAlgoliaSearchConfigured } from '@/plugins/algolia/client'
